import { Api, Model, ModelsApi } from '@ps-int/transport';

import { selectMultiplePromosModelResponseSchema } from '../../../../../common/helpers/promozavr/promozavr-schema';
import { isSelectPromoModelResponse } from '../../../../../common/helpers/promozavr/promozavr-validation';
import { PROMOZAVR_MODEL, PROMOZAVR_SELECT_MULTIPLE_PROMOS_MODEL } from './types';
import type {
    SelectMultiplePromosModelInput,
    SelectMultiplePromosModelResponse,
    SelectPromoModelInput,
    SelectPromoModelResponse,
} from './types';

type PromozavrModels = {
    [PROMOZAVR_MODEL]: Model<SelectPromoModelInput, SelectPromoModelResponse>;
    [PROMOZAVR_SELECT_MULTIPLE_PROMOS_MODEL]: Model<SelectMultiplePromosModelInput, SelectMultiplePromosModelResponse>;
};

type PromozavrModelsApiLike = Pick<ModelsApi<PromozavrModels>, 'request'>;

let defaultModelsApi: PromozavrModelsApiLike | undefined;

function toPromozavrErrorMessage(error: unknown) {
    if (typeof error === 'string') {
        return error;
    }

    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
        return error.message;
    }

    return 'Failed to select promo';
}

function parseSelectPromoTransportResult(result: unknown): SelectPromoModelResponse {
    if (!result || typeof result !== 'object') {
        throw new Error('Failed to select promo');
    }

    if ('error' in result) {
        throw new Error(toPromozavrErrorMessage(result.error));
    }

    if (!('data' in result) || !isSelectPromoModelResponse(result.data)) {
        throw new Error('Failed to select promo');
    }

    return result.data;
}

function parseSelectMultiplePromosTransportResult(result: unknown): SelectMultiplePromosModelResponse {
    if (!result || typeof result !== 'object') {
        throw new Error('Failed to select promos');
    }

    if ('error' in result) {
        throw new Error(toPromozavrErrorMessage(result.error));
    }

    if (!('data' in result)) {
        throw new Error('Failed to select promos');
    }

    const parsedData = selectMultiplePromosModelResponseSchema.safeParse(result.data);

    if (!parsedData.success) {
        throw new Error('Failed to select promos');
    }

    return parsedData.data;
}

export function createPromozavrModelsApi(): PromozavrModelsApiLike {
    const api = new Api('/api/models');

    return new ModelsApi<PromozavrModels>(api, {
        modelsQueryParameter: '_models',
    });
}

function getDefaultPromozavrModelsApi(): PromozavrModelsApiLike {
    if (!defaultModelsApi) {
        defaultModelsApi = createPromozavrModelsApi();
    }

    return defaultModelsApi;
}

export function createPromozavrApi(modelsApi?: PromozavrModelsApiLike) {
    return {
        async selectPromo(requestParams: SelectPromoModelInput): Promise<SelectPromoModelResponse> {
            const [result] = await (modelsApi ?? getDefaultPromozavrModelsApi()).request([
                {
                    name: PROMOZAVR_MODEL,
                    params: requestParams,
                },
            ]);

            return parseSelectPromoTransportResult(result);
        },
        async selectMultiplePromos(
            requestParams: SelectMultiplePromosModelInput
        ): Promise<SelectMultiplePromosModelResponse> {
            const [result] = await (modelsApi ?? getDefaultPromozavrModelsApi()).request([
                {
                    name: PROMOZAVR_SELECT_MULTIPLE_PROMOS_MODEL,
                    params: requestParams,
                },
            ]);

            return parseSelectMultiplePromosTransportResult(result);
        },
    };
}

const defaultPromozavrApi = createPromozavrApi();

export const { selectMultiplePromos, selectPromo } = defaultPromozavrApi;
